/**
 * Persistência do par (access_token, refresh_token).
 *
 * "Remember me" decide entre localStorage (persiste entre sessões) e
 * sessionStorage (some quando fecha a aba).
 *
 * Lição do legado (`AGENTS.md` §10): AuthContext do legado lia de
 * localStorage no boot mas Login gravava em sessionStorage quando
 * "remember" estava desmarcado — divergência que dava sessões fantasma.
 * Aqui leitura E gravação consultam ambos os storages em ordem (local
 * primeiro), com gravação em UM só baseado em `remember`.
 *
 * Chaves: `td.access_token`, `td.refresh_token`, `td.username`,
 *        `td.auth_storage` (sentinela: "local" | "session" pra indicar
 *         qual storage tem o par válido — evita ler stale do outro).
 */

const KEY_ACCESS = 'td.access_token';
const KEY_REFRESH = 'td.refresh_token';
const KEY_USERNAME = 'td.username';
const KEY_STORAGE = 'td.auth_storage';

export type StorageKind = 'local' | 'session';

function safeStorage(kind: StorageKind): Storage | null {
  // Privacy mode, embed, navegação privada etc. podem throw em acesso.
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

export type AuthBundle = {
  accessToken: string;
  refreshToken: string;
  username: string;
};

/**
 * Lê bundle + indica qual storage o continha — TD26-31 precisa preservar
 * essa decisão ao rotacionar tokens (não mover "remember=true" pra session
 * só porque deu refresh).
 */
export function loadAuthWithKind(): { bundle: AuthBundle; kind: StorageKind } | null {
  for (const kind of ['local', 'session'] as const) {
    const s = safeStorage(kind);
    if (!s) continue;
    const access = s.getItem(KEY_ACCESS);
    const refresh = s.getItem(KEY_REFRESH);
    const username = s.getItem(KEY_USERNAME);
    if (access && refresh && username) {
      return { bundle: { accessToken: access, refreshToken: refresh, username }, kind };
    }
  }
  return null;
}

export function loadAuth(): AuthBundle | null {
  return loadAuthWithKind()?.bundle ?? null;
}

export function saveAuth(bundle: AuthBundle, remember: boolean): void {
  const kind: StorageKind = remember ? 'local' : 'session';
  const s = safeStorage(kind);
  if (!s) return;
  // Limpa o outro storage pra evitar dupla-leitura ambígua.
  clearAuth();
  s.setItem(KEY_ACCESS, bundle.accessToken);
  s.setItem(KEY_REFRESH, bundle.refreshToken);
  s.setItem(KEY_USERNAME, bundle.username);
  s.setItem(KEY_STORAGE, kind);
}

/**
 * Atualiza tokens preservando o storage atual — usado pelo refresh rotation
 * (TD26-31). Se não há storage detectável (privacy mode etc), no-op silencioso
 * — store em memória continua funcionando até a aba fechar.
 */
export function updateTokens(bundle: AuthBundle): void {
  const current = loadAuthWithKind();
  if (!current) return;
  const s = safeStorage(current.kind);
  if (!s) return;
  s.setItem(KEY_ACCESS, bundle.accessToken);
  s.setItem(KEY_REFRESH, bundle.refreshToken);
  s.setItem(KEY_USERNAME, bundle.username);
  // KEY_STORAGE preservado — mesma kind.
}

export function clearAuth(): void {
  for (const kind of ['local', 'session'] as const) {
    const s = safeStorage(kind);
    if (!s) continue;
    s.removeItem(KEY_ACCESS);
    s.removeItem(KEY_REFRESH);
    s.removeItem(KEY_USERNAME);
    s.removeItem(KEY_STORAGE);
  }
}
